import { useState } from "react"
import { DateRangePicker } from "../../shared/dateRangePicker"
import { SearchSelect } from "../../shared/searchSelect"
import { SearchFiltersModal } from "../../shared/searchFiltersModal"
import stringHelpers from "../../../utilities/stringHelpers"
import { toDateString } from "../../../utilities/dateUtilities"
import api from "../../api/api"
import { TagSelector } from "../../shared/tagSelector"
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities"
import arrayUtilities from "../../../utilities/arrayUtilities"

export const PcnPaymentsDataFiltersModal = ({
    showModal,
    handleClose,
    handleConfirm,
    initialFromDate,
    initialToDate,
    initialDateSearchMode,
    initialDateSearchType,
    initialClientId,
    initialSiteId,
    initialSitesByClient,
    initialSelectedTags = [],
    initialAppliedFilters,
    clients = [],
    sites = [],
    tags = [],
    organisationId,
    token
}) => {
    const [fromDate, setFromDate] = useState(initialFromDate)
    const [toDate, setToDate] = useState(initialToDate)
    const [dateSearchMode, setDateSearchMode] = useState(initialDateSearchMode)
    const [dateSearchType, setDateSearchType] = useState(initialDateSearchType)
    const [clientId, setClientId] = useState(initialClientId)
    const [siteId, setSiteId] = useState(initialSiteId)
    const [sitesByClient, setSitesByClient] = useState(initialSitesByClient)
    const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

    const clearLocalFilters = () => {
        setFromDate(null)
        setToDate(null)
        setDateSearchMode(1)
        setDateSearchType(0)
        setClientId("")
        setSiteId("")
        setSitesByClient(sites)
        setSelectedTags([])
        setAppliedFilters({
            "Date Mode": "Allow Overlap",
            "Search By": "Breach Issue Date"
        })
    }

    const handleTagsChange = tagSelections => {
        setSelectedTags(tagSelections)
        setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
    }

    return (
        <SearchFiltersModal
            title="Filter Results"
            handleClose={ handleClose }
            handleConfirm={ () => handleConfirm({ fromDate, toDate, dateSearchMode, dateSearchType, clientId, siteId, sitesByClient, selectedTags, appliedFilters }) }
            showModal={ showModal }
            key="pcn-payments-data-filters-modal"
            handleReset={ clearLocalFilters }
        >
            <div className="w-100">
                <div className="mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
                    <DateRangePicker
                        fromDate={ fromDate }
                        toDate={ toDate }
                        setDateRange={ (firstDate, lastDate, presetLabel) => {
                            const isFirstDateEmpty = firstDate === null || firstDate === ""
                            const isLastDateEmpty = lastDate === null || lastDate === ""
                            setFromDate(isFirstDateEmpty ? null : firstDate);
                            setToDate(isLastDateEmpty ? null : lastDate);
                            if (!stringHelpers.isNullOrEmpty(presetLabel)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters.From
                                    delete updatedFilters.To
                                    updatedFilters["Date Range"] = presetLabel
                                    return updatedFilters
                                })
                            }
                            else {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    if (!isFirstDateEmpty && !isLastDateEmpty) {
                                        delete updatedFilters.From
                                        delete updatedFilters.To
                                        updatedFilters["Date Range"] = `${ toDateString(new Date(firstDate)) } - ${ toDateString(new Date(lastDate)) }`
                                    } else {
                                        if (!isFirstDateEmpty) {
                                            updatedFilters.From = toDateString(new Date(firstDate))
                                        } else {
                                            delete updatedFilters.From
                                        }
                                        if (!isLastDateEmpty) {
                                            updatedFilters.To = toDateString(new Date(lastDate))
                                        } else {
                                            delete updatedFilters.To
                                        }
                                        delete updatedFilters["Date Range"]
                                    }
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            }
                        } }
                        showClearOption
                        hideLabel
                        fullWidth
                        dropdownPositionRight
                    />
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Date Mode</label>
                    <select
                        id="date-search-mode-selector"
                        className="border rounded px-2 py-1 w-100"
                        value={ dateSearchMode === null ? "" : dateSearchMode }
                        onChange={ e => {
                            const value = Number(e.target.value)
                            setDateSearchMode(value)
                            setAppliedFilters(prevState => ({
                                ...prevState,
                                "Date Mode": value === 0 ? "Strict" : "Allow Overlap"
                            }))
                        } }
                    >
                        <option value="0">Strict</option>
                        <option value="1">Allow Overlap</option>
                    </select>
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Search By</label>
                    <select
                        id="date-search-type-selector"
                        className="border rounded px-2 py-1 w-100"
                        value={ dateSearchType === null ? "" : dateSearchType }
                        onChange={ e => {
                            const value = Number(e.target.value)
                            setDateSearchType(value)
                            setAppliedFilters(prevState => ({
                                ...prevState,
                                "Search By": value === 0 ? "Breach Issue Date" : "Payment Date"
                            }))
                        } }
                    >
                        <option value="0">Breach Issue Date</option>
                        <option value="1">Payment Date</option>
                    </select>
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Client</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={ [{ value: "", label: "All" }, ...clients.map(item => ({ value: item.id, label: item.name }))] }
                        onSelect={ value => {
                            setClientId(value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setSitesByClient(sites)
                                setSiteId("")
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["Client"]
                                    delete updatedFilters["Site"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } else {
                                api.getClientSites(
                                    data => {
                                        setSitesByClient(data)
                                        setSiteId("")
                                    },
                                    error => console.log(error),
                                    value,
                                    organisationId,
                                    token
                                )
                                const selectedClient = clients.find(item => item.id === value)
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState, "Client": selectedClient?.name }
                                    delete updatedFilters["Site"]
                                    return updatedFilters
                                })
                            }
                        } }
                        selected={ clientId }
                    />
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Site</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={ [{ value: "", label: "All" }, ...sitesByClient.map(item => ({ value: item.id, label: item.name }))] }
                        onSelect={ value => {
                            setSiteId(value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["Site"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } else {
                                const selectedSite = sitesByClient.find(item => item.id === value)
                                setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSite?.name }))
                            }
                        } }
                        selected={ siteId }
                    />
                </div>

                { !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <label className="m-0 mr-2 mb-1 nowrap">Tags</label>
                        <button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
                    </div>
                    <TagSelector
                        onChange={ handleTagsChange }
                        selectedTags={ selectedTags }
                        tags={ tags }
                    />
                </div> }
            </div>
        </SearchFiltersModal>
    )
}
